import { makeStyles } from '@material-ui/core'

/**
 * ONE column constant, shared by the header, the row and the skeleton.
 *
 * portal-ux-conventions §2 asks for this explicitly so the three cannot drift.
 * The reference screen (ChannelsTable.styles.ts) repeats its ratio literal in
 * two places; that is the thing the rule exists to prevent, so it is not copied
 * here.
 */
// Leading fixed track for the selection checkbox. Fixed, not fractional: a
// checkbox has one size, and giving it an `fr` would let it grow on a wide
// viewport into a column of empty space.
// Fixed 64px for priority, like the checkbox track: a P-value is four
// characters and giving it an `fr` would let it grow into empty space on a wide
// viewport while the title — the column that actually needs room — keeps its
// share.
const COLUMNS = '48px 64px 4fr 8fr 6fr 5fr 5fr 4fr 1fr'

/**
 * How many skeleton bars a loading row needs: one per track, minus the
 * trailing actions track which the skeleton fills with an empty div.
 *
 * DERIVED, not a second copy of the number. The count has now been wrong twice
 * — once when the checkbox track was added, once when the priority track was —
 * and both times the failure was silent: the grid still rendered, every bar
 * just sat one column from where its real content lands, and the layout jumped
 * when the data arrived. A hand-maintained integer that must track COLUMNS by
 * convention is a convention nothing enforces.
 */
export const SKELETON_BAR_COUNT = COLUMNS.split(' ').length - 1

const useStyles = makeStyles((theme) => ({
  // A stripe, not a coloured chip. Priority is scanned down a column of forty
  // rows, and a chip per row turns the list into a wall of colour that stops
  // discriminating; a thin leading rule reads at a glance and leaves the row's
  // own tone to the state Chip.
  //
  // The colour is NOT the only carrier — the P-value is rendered as text beside
  // it. Colour alone would fail 1.4.1, and the same operators read this list in
  // Farsi where a colour convention borrowed from elsewhere carries less.
  // Plain `gap`, no margin fallback. `npx browserslist` resolves this project
  // to Safari 15.6+ and Firefox 115+; flexbox gap shipped in Safari 14.1 and
  // Firefox 63, so every target supports it and a fallback would be code that
  // can never run.
  priorityCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5)
  },
  priorityStripe: {
    width: 3,
    height: '1.1em',
    borderRadius: 2,
    flex: '0 0 auto'
  },
  // borderInlineStart, not borderLeft: the stripe must lead the value in Farsi
  // too, and a physical side would put it on the trailing edge there.
  priorityP1: { backgroundColor: theme.palette.error.main },
  priorityP2: { backgroundColor: theme.palette.warning.main },
  priorityP3: { backgroundColor: theme.palette.info.main },
  priorityP4: { backgroundColor: theme.palette.action.disabled },

  root: {},
  tableHead: {
    // Grid children default to min-width:auto, which means a long unbreakable
    // string sets the column's floor and pushes every other column aside — one
    // encoded identifier was enough to wreck the whole row. min-width:0 lets the
    // track shrink to its fr share, and overflow-wrap breaks the token inside its
    // own cell instead of outside it.
    '& > div': {
      minWidth: 0,
      overflowWrap: 'anywhere',
      // A cell holding a Chip beside text: Chip is inline-flex, Typography
      // renders a block <p>, so without flex the text drops to a second line
      // and that row alone is two lines tall. Visible in the reported
      // screenshot as "Needs Adotel" sitting above "Unassigned".
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 4
    },
    width: '100%',
    display: 'grid',
    gridTemplateColumns: COLUMNS,
    color: theme.palette.text.secondary,
    alignItems: 'center',
    padding: '0 0 0 12px',
    marginBottom: 4,
    marginTop: 4,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12
  },
  tableRow: {
    // Grid children default to min-width:auto, which means a long unbreakable
    // string sets the column's floor and pushes every other column aside — one
    // encoded identifier was enough to wreck the whole row. min-width:0 lets the
    // track shrink to its fr share, and overflow-wrap breaks the token inside its
    // own cell instead of outside it.
    '& > div': {
      minWidth: 0,
      overflowWrap: 'anywhere',
      // A cell holding a Chip beside text: Chip is inline-flex, Typography
      // renders a block <p>, so without flex the text drops to a second line
      // and that row alone is two lines tall. Visible in the reported
      // screenshot as "Needs Adotel" sitting above "Unassigned".
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 4
    },
    display: 'grid',
    gridTemplateColumns: COLUMNS,
    minHeight: 72,
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    borderRadius: 20,
    // No `cursor: pointer`: only the ticket number is a link, so a pointer
    // across the full row width promises a click target that does not exist.
    // The mobile card IS fully clickable; the desktop row is not.
    paddingLeft: 12,
    marginBottom: 4,
    transition: 'background-color 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: theme.palette.background.paper
    }
  },
  // A closed ticket stays readable: the state Chip already carries the meaning
  // in text, so the row is de-emphasised rather than faded to the point where
  // secondary text drops below contrast minimums.
  // Two lines keeps most titles whole while pinning row height. minHeight is a
  // floor, not a ceiling, so an unclamped title grows its row and leaves the
  // table ragged — half of what reads as "messy". Not one line: the title is
  // the primary identifying content. The full text is one click away.
  titleCell: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  terminalRow: {
    opacity: 0.85
  },
  refreshButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  skeleton: {
    marginInlineEnd: theme.spacing(2)
  },
  noDataImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4)
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2)
  }
}))

export default useStyles
